import { appInsight, collectProperties } from 'components/appInsight';
import { ModalHeader } from 'components/modals/components/modalHeader';
import { UnRegisterModal } from 'components/modals/unRegisterModal';
import { UploadDocumentModal } from 'components/modals/uploadDocumentModal';
import { getContentUploadDocumentModal } from 'components/modals/uploadDocumentModal/utils/functions';
import { Spinner } from 'components/spinner';
import { useLazyGetSessionDataQuery, useSignUpSpidMutation } from 'features/api/userSlice';
import { setIsOpenUploadDocumentModal } from 'features/modal/modalSlice';
import { selectIsOpenUnregistrationModal, selectIsOpenUploadDocumentModal } from 'features/modal/selectors';
import { FiscalCode } from 'features/resetPassword/components/fiscalCode';
import { AccessData } from 'features/signup/components/accessData';
import { getLabelAccessData } from 'features/signup/components/accessData/utils/functions';
import { Contacts } from 'features/signup/components/contacts';
import { getContactsLabel } from 'features/signup/components/contacts/utils/functions';
import { DocumentDataForm } from 'features/signup/components/documentData';
import { getDocumentDataLabel } from 'features/signup/components/documentData/utils/functions';
import { LimitMaximal } from 'features/signup/components/limitMaximal';
import { getLabelMaximals } from 'features/signup/components/limitMaximal/utils/functions';
import { PersonalInfo } from 'features/signup/components/personalInfo';
import { KEY_COOKIE_SIGNUP } from 'features/signup/components/personalInfo/utils/constant';
import { getPersonalDataLabel } from 'features/signup/components/personalInfo/utils/functions';
import { PrivacyConsents } from 'features/signup/components/privacyConsents';
import { getLabelPrivacyConsents } from 'features/signup/components/privacyConsents/utils/functions';
import { PromoBonus } from 'features/signup/components/promoBonus';
import { getLabelsPromoBonus } from 'features/signup/components/promoBonus/utils/functions';
import { RegistrationUncompleted } from 'features/signup/components/registrationUnCompleted';
import { getRegistrationUncompletedLabel } from 'features/signup/components/registrationUnCompleted/utils/functions';
import { StepperComposer } from 'features/signup/components/stepperComposer';
import {
  setAccessData,
  setExtraData,
  setPromoCode,
  setRegistrationFromPage,
  setSignup,
  setTriggerForm,
} from 'features/signup/signup';
import { useGeoInfo, useSignupDataConverter } from 'features/signup/signUpHooks';
import { SignupState } from 'features/signup/types';
import { RegistrationFromPage, RegistrationValue } from 'features/signup/types/signUpEnum';
import { useIsSeamlessMode } from 'hooks';
import useAgentDetect from 'hooks/useAgentDetect';
import { useGetLabelByKey } from 'hooks/useLingUI';
import useStepper from 'hooks/useStepper';
import { RootState, useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { DatoCmsSignUp } from 'lib/datoCms/queries/getAllSignUpLabels';
import { DatoCmsHeader } from 'lib/datoCms/types';
import { convertInThemeLogoType } from 'lib/datoCms/utils';
import Persist, { StorageKind } from 'lib/persist';
import { deleteCookie, getCookie, setCookie } from 'lib/persist/cookie';
import { getNonce } from 'lib/policy';
import { DatoSignUpErrorsLabels } from 'lib/types/signup-errors-label';
import { NextPage } from 'next';
import withRouter, { WithRouterProps } from 'next/dist/client/with-router';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { UsersSignUpDataDto } from 'types/swagger';
import { PROVENIENCE_QUERY_PARAM } from 'utility/constant';
import { gtag, isMatch, isTruthy } from 'utility/functions';
import { getRouteByKey, PathName } from 'utility/routes';
import { useUpdateQueryParam } from './hooks/useUpdateQueryParams';
import styles from './registrati.module.scss';

const IOVATION_URL = process.env.NEXT_PUBLIC_IOVATION_URL;

const registrationNotRetryErrorNumbers = [1025, 1026, 1027, 1298, -4];
const registrationRestartErrorNumbers = [
  1028, 1032, 1100, 1200, 1201, 1232, 1286, 1299, 1300, 1301, 1302, 1303, 1304, 1417, 1418, 1419, 1420, 1431, -71, -61,
  -45, -40, -35, -28, -27, 540, 552,
];
const registrationRetrySubtimErrorNumbers = [1029];

type Props = {
  header: DatoCmsHeader;
  signUpLabels: DatoCmsSignUp;
  signUpLabelsError: DatoSignUpErrorsLabels;
} & WithRouterProps;

export type RegistrationStatus = {
  state: RegistrationValue;
  description?: string;
  esito?: number;
};

const PageContent: NextPage<Props> = ({ header, signUpLabels, signUpLabelsError, router }) => {
  const dispatch = useAppDispatch();
  const [isYourDataVisible, setIsYourDataVisible] = useState(false);
  // const { session, status } = useInternalSession();
  const [trigger] = useSignUpSpidMutation();
  const [retrieveRedisSession] = useLazyGetSessionDataQuery();
  const labelContinua = useGetLabelByKey('continua');

  const { getTownships } = useGeoInfo();
  const { merge } = useSignupDataConverter();
  const updateQueryParam = useUpdateQueryParam();

  const isLoaded = useRef(false);
  const { push } = useRouter();
  const searchParams = useSearchParams();
  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);
  const state = useSelector((store: RootState) => store.signup);
  const [registrationValueCompleted, setRegistrationValueCompleted] = useState<RegistrationStatus>({
    state: state.isSpid ? RegistrationValue.LOADING : RegistrationValue.DEFAULT,
  });

  const [isHideRetryBtn, setIsHideRetryBtn] = useState<boolean>(false);
  const isSeamlessMode = useIsSeamlessMode();

  const { isMac } = useAgentDetect();

  useEffect(() => {
    if (
      registrationValueCompleted.esito &&
      registrationNotRetryErrorNumbers.includes(registrationValueCompleted.esito)
    ) {
      setIsHideRetryBtn(true);
    }
  }, [registrationValueCompleted.esito]);

  const { steps, isActiveCf } = useMemo(() => {
    const isActiveCf = isTruthy(process.env.NEXT_PUBLIC_ISACTIVE_STEP_CF_REGISTRAZIONE);

    const steps = [
      { label: signUpLabels.labelFirstStep, isActiveStep: false },
      { label: signUpLabels.labelSecondStep, isActiveStep: false },
      { label: signUpLabels.labelThirdStep, isActiveStep: false },
      { label: signUpLabels.labelFourthStep, isActiveStep: false },
      { label: signUpLabels.labelSixthStep, isActiveStep: false },
      { label: signUpLabels.labelFifthStep, isActiveStep: false },
      { label: signUpLabels.labelSeventhStep, isActiveStep: false },
    ];

    if (isActiveCf) {
      steps.unshift({ label: signUpLabels.labelEighthStep, isActiveStep: false });
    }

    steps[0].isActiveStep = true;

    return { steps, isActiveCf };
  }, [signUpLabels]);

  const { stepsData, setNextStep, setPreviousStep, getActiveStepIndex, setActiveStep } = useStepper(steps);

  const locationQuery = useMemo(() => new URLSearchParams(searchParams.toString()).toString(), [searchParams]);

  useEffect(() => {
    switch (registrationValueCompleted.state) {
      case RegistrationValue.DEFAULT: {
        if (!!locationQuery) {
          Persist(StorageKind.session).setItem('signup-query', locationQuery);
        }
        break;
      }
      case RegistrationValue.SUCCESS: {
        Persist(StorageKind.session).removeItem('signup-query');
        break;
      }
    }
  }, [locationQuery, registrationValueCompleted.state]);

  const handleClickOnRetryBtn = useCallback(
    (esito: number) => {
      if (!esito) {
        return;
      }

      if (registrationRestartErrorNumbers.includes(esito)) {
        const { username, secretAnswer, secretQuestion } = state;
        setIsYourDataVisible(true);
        dispatch(
          setAccessData({
            username,
            password: '',
            secretAnswer,
            secretQuestion,
          })
        );

        setActiveStep(0);
        setRegistrationValueCompleted({ state: RegistrationValue.DEFAULT });
        dispatch(setTriggerForm(false));

        if (Persist(StorageKind.session).hasItem('signup-query')) {
          const signupQuery = Persist(StorageKind.session).getItem('signup-query');
          push(`${registrationUrl}?${signupQuery}`);
        }
      } else if (registrationRetrySubtimErrorNumbers.includes(esito)) {
        setIsYourDataVisible(true);
        setRegistrationValueCompleted({ state: RegistrationValue.DEFAULT });
        dispatch(setTriggerForm(true));
      }
    },
    [dispatch, push, registrationUrl, setActiveStep, state]
  );

  const formsComponents = useMemo(() => {
    const result = [
      <AccessData
        labels={getLabelAccessData({ ...signUpLabels, ...signUpLabelsError })}
        key="accessData"
        next={setNextStep}
        isYouDataVisible={isYourDataVisible}
      />,
      <PromoBonus
        labels={getLabelsPromoBonus(signUpLabels)}
        key="promoBonus"
        next={setNextStep}
        back={setPreviousStep}
      />,
      <LimitMaximal labels={getLabelMaximals(signUpLabels)} key="maximals" next={setNextStep} back={setPreviousStep} />,
      <PrivacyConsents
        labels={getLabelPrivacyConsents(signUpLabels)}
        key="privacyConsents"
        next={setNextStep}
        back={setPreviousStep}
      />,
      <PersonalInfo
        labels={getPersonalDataLabel(signUpLabels)}
        key="personalData"
        next={setNextStep}
        back={setPreviousStep}
        setState={setRegistrationValueCompleted}
      />,
      <Contacts labels={getContactsLabel(signUpLabels)} key="contacts" next={setNextStep} back={setPreviousStep} />,
      <DocumentDataForm
        labels={getDocumentDataLabel(signUpLabels)}
        key="documentDataForm"
        back={setPreviousStep}
        setState={setRegistrationValueCompleted}
        labelLastStep={signUpLabels.labelButtonLastStep}
      />,
    ];

    if (isActiveCf) {
      result.unshift(
        <FiscalCode
          labels={{
            continueButton: labelContinua!,
            labelInput: signUpLabels.labelFiscalcode,
            labelPlaceholder: signUpLabels.placeholderFiscalcode,
            title: signUpLabels.titleFiscalcode,
            subtitle: signUpLabels.descriptionFiscalcode,
          }}
          next={setNextStep}
          isCheckRegistration
          errorCf={signUpLabels.erroreCodiceFiscaleRegistrato}
          key="fiscalCode"
        />
      );
    }

    return result;
  }, [isActiveCf, isYourDataVisible, labelContinua, signUpLabels, signUpLabelsError, setNextStep, setPreviousStep]);

  useEffect(() => {
    const codPromo = (router.query['cod_promo'] ||
      router.query['COD_PROMO'] ||
      router.query['cod_prom'] ||
      router.query['COD_PROM']) as string;

    if (codPromo) {
      dispatch(setPromoCode({ promoCode: codPromo }));
    }
    if (router.query['btag']) setCookie('btag', router.query['btag'] as string, 7 * 24 * 60 * 60);

    dispatch(
      setExtraData({
        btag: getCookie('btag') ?? (router.query.btag as string),
        provenienceCode: router.query[PROVENIENCE_QUERY_PARAM] as string,
      })
    );
  }, [router, dispatch]);

  useEffect(() => {
    gtag(
      {
        section: 'Registration',
        detail: 'Registration Start',
        event: 'Registration Start',
        eventCategory: 'Registration',
      },
      true
    );

    document.body.style.overflowY = 'hidden';

    setTimeout(() => {
      document.body.classList.remove('bodyOsDesktop');
    }, 1000);

    return () => {
      document.body.style.overflowY = '';
      if (isMac) {
        setTimeout(() => {
          document.body.classList.add('bodyOsDesktop');
        }, 1000);
      }
    };
  }, [isMac]);

  const handleSpid = useCallback(async () => {
    let data = {} as SignupState;
    merge(data, state, true);

    // SCENARIO WHERE THE USER GO BACK FROM SPID
    const sessionKey = getCookie(KEY_COOKIE_SIGNUP);
    if (sessionKey) {
      const townships = await getTownships();
      const sessionData = (await retrieveRedisSession(sessionKey).unwrap()) as UsersSignUpDataDto;
      const body = {
        ...sessionData,
        FiscalCode: undefined,
      };
      merge(data, body);

      // city -> province
      let provExp = RegExp(`^${data.province}$`.toLowerCase(), 'gim');
      if (!data.province || !townships.some((x) => isTruthy(x.provincia.match(provExp)?.length))) {
        const cityExp = RegExp(`^${data.city}$`.toLowerCase(), 'gim');
        const lookupComune = townships.find((x) => isTruthy(x.comune.match(cityExp)?.length));
        if (lookupComune) {
          Reflect.set(data, 'province', lookupComune.provincia);
        }
      }

      // province -> sigla
      if ((data.province ?? '').length > 2) {
        provExp = RegExp(`^${data.province}$`.toLowerCase(), 'gim');
        const lookupProvincia = townships.find((x) => x.provincia.match(provExp));
        if (lookupProvincia) {
          Reflect.set(data, 'province', lookupProvincia.sigla);
        }
      }

      // domicileCity -> domicileProvince
      let domicileProvinceExp = RegExp(`^${data.domicileProvince}$`.toLowerCase(), 'gim');
      if (!data.domicileProvince || !townships.some((x) => isTruthy(x.provincia.match(domicileProvinceExp)?.length))) {
        const domicileProvinceExp = RegExp(`^${data.domicileCity}$`.toLowerCase(), 'gim');
        const lookupComune = townships.find((x) => isTruthy(x.comune.match(domicileProvinceExp)?.length));
        if (lookupComune) {
          Reflect.set(data, 'domicileProvince', lookupComune.provincia);
        }
      }

      // domicileProvince -> sigla
      if ((data.domicileProvince ?? '').length > 2) {
        domicileProvinceExp = RegExp(`^${data.domicileProvince}$`.toLowerCase(), 'gim');
        const lookupProvincia = townships.find((x) => x.provincia.match(domicileProvinceExp));
        if (lookupProvincia) {
          Reflect.set(data, 'domicileProvince', lookupProvincia.sigla);
        }
      }

      dispatch(setSignup(data));
      setActiveStep(4);
    }
    if (data.isSpid) {
      dispatch(setRegistrationFromPage(RegistrationFromPage.SPID));
      if (data.spidCode == 1024) {
        try {
          const response = await trigger({
            ...data,
            btag: data.btag || Persist(StorageKind.session).getItem('btag') || '',
            provenienceCode:
              data.provenienceCode || Persist(StorageKind.session).getItem(PROVENIENCE_QUERY_PARAM) || '',
          }).unwrap();
          //Added for SIA-2604
          if (response.cod_contratto) {
            localStorage.setItem('cod_contratto', response.cod_contratto);
          }
          deleteCookie(KEY_COOKIE_SIGNUP);
          setRegistrationValueCompleted({ state: RegistrationValue.SUCCESS });
        } catch (error) {
          setRegistrationValueCompleted({
            state: RegistrationValue.ERROR,
            description: error.data.response.descrizione ?? error.data.description,
            esito: error?.data?.response?.esito,
          });
        }
      } else {
        // ERROR DURING THE SPID PROCESS
        appInsight?.trackException({
          exception: new Error('SPID Errore'),
          properties: collectProperties(true, { erroCode: state.spidCode }),
        });
        setRegistrationValueCompleted({ state: RegistrationValue.DEFAULT });
        setActiveStep(4);
      }
    } else {
      dispatch(setRegistrationFromPage(RegistrationFromPage.REGISTRATION));
    }
    deleteCookie(KEY_COOKIE_SIGNUP);
  }, [state, getTownships, retrieveRedisSession, merge, dispatch, setActiveStep, trigger]);

  useEffect(() => {
    if (!isLoaded.current) {
      isLoaded.current = true;
      handleSpid();
    }
  }, [handleSpid]);

  useEffect(() => {
    if (registrationValueCompleted.state !== RegistrationValue.SUCCESS) {
      return;
    }

    push(!isSeamlessMode ? `${registrationUrl}/welcome` : `${registrationUrl}/welcome?seamless=true`);
  }, [registrationValueCompleted.state, registrationUrl, push, isSeamlessMode]);

  const isOpenUploadDocumentModal = useTypedSelector(selectIsOpenUploadDocumentModal);
  const isOpenUnregistrationModal = useTypedSelector(selectIsOpenUnregistrationModal);
  const modalUnRegistration = useMemo(
    () => ({
      title: signUpLabels.modalTitle,
      description: signUpLabels.modalDescription,
      labelUnRegistration: signUpLabels.labelReasonUnregistration,
      reasonUnregistration: signUpLabels.reasonUnregistration,
      buttonContinueRegistration: signUpLabels.buttonContinueRegistration,
      buttonUnRegistration: signUpLabels.buttonUnregistration,
    }),
    [
      signUpLabels.buttonContinueRegistration,
      signUpLabels.buttonUnregistration,
      signUpLabels.labelReasonUnregistration,
      signUpLabels.modalDescription,
      signUpLabels.modalTitle,
      signUpLabels.reasonUnregistration,
    ]
  );

  const stepId = useMemo(() => getActiveStepIndex(), [getActiveStepIndex]);

  useEffect(() => {
    const currentComponentKey = formsComponents[stepId].key;
    if (currentComponentKey) {
      updateQueryParam(currentComponentKey);
    }
  }, [formsComponents, stepId, updateQueryParam]);

  return (
    <div className={styles.container}>
      <ModalHeader
        logoBrand={convertInThemeLogoType(header.logoBrand[0])}
        modalCategory="registration-modal"
        label="Registrazione"
        modalType="primary"
        customRoute={registrationValueCompleted.state === RegistrationValue.SUCCESS ? '/' : undefined}
        showCloseButton={!isSeamlessMode}
        isSeamlessMode={isSeamlessMode}
        headerClass={isSeamlessMode ? styles.headerSeamless : undefined}
      />
      {registrationValueCompleted.state === RegistrationValue.DEFAULT && (
        <StepperComposer
          sidebarData={{
            stepsData: stepsData,
            welcomeLabel: signUpLabels.tipsWelcome,
            chatTriggerLabel: signUpLabels.textChatTrigger,
          }}
        >
          <React.Fragment>
            {isMatch(IOVATION_URL, '^http') && <Script src={IOVATION_URL} nonce={getNonce()} />}
            {formsComponents[stepId]}
          </React.Fragment>
        </StepperComposer>
      )}
      {registrationValueCompleted.state === RegistrationValue.ERROR && (
        <RegistrationUncompleted
          labels={getRegistrationUncompletedLabel(signUpLabels)}
          descriptionError={registrationValueCompleted.description!}
          hideRetryBtn={isHideRetryBtn}
          onTryAgainClicked={() => {
            handleClickOnRetryBtn(registrationValueCompleted.esito!);
          }}
        />
      )}
      {registrationValueCompleted.state === RegistrationValue.LOADING && (
        <div className={styles.spinnerContainer}>
          <Spinner className={styles.spinner} />
        </div>
      )}
      <UnRegisterModal isOpenModal={isOpenUnregistrationModal} contentModal={modalUnRegistration} />
      {isOpenUploadDocumentModal && (
        <UploadDocumentModal
          isOpenModal={isOpenUploadDocumentModal}
          content={getContentUploadDocumentModal(signUpLabels)}
          logoBrand={convertInThemeLogoType(header.logoBrand[0])}
          closeModal={() => {
            dispatch(setIsOpenUploadDocumentModal(false));
          }}
        />
      )}
    </div>
  );
};

export default withRouter(PageContent);
